/**
 * Cache validation and cleanup utilities to prevent authentication issues
 */
import { authStateManager } from "./authStateManager";
import { logger } from "../../utils/logger";

export class CacheValidator {
  private static instance: CacheValidator;
  private validationInterval: NodeJS.Timeout | null = null;

  private constructor() {}

  public static getInstance(): CacheValidator {
    if (!CacheValidator.instance) {
      CacheValidator.instance = new CacheValidator();
    }
    return CacheValidator.instance;
  }

  /**
   * Validate and clean up cache on app startup
   */
  public async validateOnStartup(): Promise<void> {
    try {
      logger.info("Validating cache on startup...");

      // Check for authentication state inconsistencies
      const validation = authStateManager.validateAuthState();

      if (!validation.isValid) {
        logger.warn("Cache validation found issues:", validation.issues);

        // Try to fix inconsistencies
        const fixed = authStateManager.fixAuthStateInconsistencies();
        if (!fixed) {
          logger.warn(
            "Could not fix cache inconsistencies, clearing auth state"
          );
          authStateManager.clearAuthState();
        }
      }

      // Check for localStorage quota issues
      const { isLocalStorageGettingFull } = await import("./localStorageUtils");
      if (isLocalStorageGettingFull(85)) {
        logger.warn(
          "localStorage is getting full, clearing non-essential data"
        );
        const { clearNonEssentialData } = await import("./localStorageUtils");
        clearNonEssentialData();
      }

      // Clear any stale retry counters
      localStorage.removeItem("networkErrorRetryCount");
      localStorage.removeItem("tokenRefreshing");

      logger.info("Cache validation completed");
    } catch (error) {
      logger.error("Error during cache validation:", error);
    }
  }

  /**
   * Start periodic cache validation
   */
  public startPeriodicValidation(intervalMs: number = 300000): void {
    // 5 minutes default
    if (this.validationInterval) {
      clearInterval(this.validationInterval);
    }

    this.validationInterval = setInterval(async () => {
      try {
        const validation = authStateManager.validateAuthState();
        if (!validation.isValid) {
          logger.warn("Periodic validation found issues:", validation.issues);
          authStateManager.fixAuthStateInconsistencies();
        }
      } catch (error) {
        logger.error("Error during periodic validation:", error);
      }
    }, intervalMs);

    logger.info(`Started periodic cache validation every ${intervalMs}ms`);
  }

  /**
   * Stop periodic cache validation
   */
  public stopPeriodicValidation(): void {
    if (this.validationInterval) {
      clearInterval(this.validationInterval);
      this.validationInterval = null;
      logger.info("Stopped periodic cache validation");
    }
  }

  /**
   * Clear all caches and reset authentication state
   */
  public async clearAllCaches(): Promise<void> {
    try {
      logger.info("Clearing all caches...");

      // Clear authentication state
      authStateManager.clearAuthState();

      // Clear other localStorage data
      const keysToKeep = ["packageJsonVersion", "userStorage", "dateMMDDYYYY"];

      const allKeys = Object.keys(localStorage);
      allKeys.forEach((key) => {
        if (!keysToKeep.includes(key)) {
          try {
            localStorage.removeItem(key);
          } catch (error) {
            logger.warn(`Failed to remove ${key} from localStorage:`, error);
          }
        }
      });

      // Clear session storage
      try {
        sessionStorage.clear();
      } catch (error) {
        logger.warn("Failed to clear sessionStorage:", error);
      }

      logger.info("All caches cleared successfully");
    } catch (error) {
      logger.error("Error clearing caches:", error);
    }
  }

  /**
   * Check for and handle browser storage quota issues
   */
  public async handleStorageQuotaIssues(): Promise<boolean> {
    try {
      const { getLocalStorageUsage, clearNonEssentialData } = await import(
        "./localStorageUtils"
      );
      const usage = getLocalStorageUsage();

      if (usage.percentage > 90) {
        logger.warn(
          `localStorage usage is at ${usage.percentage.toFixed(
            2
          )}%, clearing non-essential data`
        );
        clearNonEssentialData();

        // Check again after clearing
        const newUsage = getLocalStorageUsage();
        if (newUsage.percentage > 95) {
          logger.error(
            "localStorage still too full after clearing, may cause issues"
          );
          return false;
        }
      }

      return true;
    } catch (error) {
      logger.error("Error handling storage quota issues:", error);
      return false;
    }
  }
}

// Export singleton instance
export const cacheValidator = CacheValidator.getInstance();
